import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, NgModule, SkipSelf } from '@angular/core';
import * as i1 from '@sdpp-web/logger';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { tap, finalize } from 'rxjs/operators';

/**
 * Token used by the [Telemetry Module]{@link ../modules/TelemetryModule.html} to
 * configure the loggerId in the [Telemetry Service]{@link ../injectables/TelemetryService.html}
 */
const TELEMETRY_SERVICE_CONFIG = new InjectionToken('sdpp-web-telemetry-service-config');

/**
 * Service for publish the messages from the interceptors/decorators of the Telemetry Module
 */
class TelemetryService {
    /**
     * Assign the logger when the loggingService is defined
     *
     * @param loggingService instance of the logging service
     * @param loggerId logger id for the Logging Service.
     * The token is always define by the TelemetryModule
     */
    constructor(loggingService, loggerId) {
        if (loggingService) {
            this.logger = loggingService.getLogger(loggerId);
        }
    }
    /**
     * Method to publish the information generated by the Telemetry Http Interceptor
     *
     * @param value value to be published
     */
    http(value) {
        this.publishMessage(value);
    }
    /**
     * Method to publish the information generated by the Telemetry Method Decorator
     *
     * @param value value to be published
     */
    method(value) {
        this.publishMessage(value);
    }
    /**
     * Method to publish the information generated by the Telemetry Component Decorator
     *
     * @param value value to be published
     */
    component(value) {
        this.publishMessage(value);
    }
    /**
     * Method to publish a custom message.
     * @param value value to be published
     */
    message(value) {
        this.publishMessage(value);
    }
    publishMessage(value) {
        if (this.logger) {
            this.logger.info(value);
        }
        else {
            // eslint-disable-next-line no-console
            console.log(value);
        }
    }
}
/** @nocollapse */ /** @nocollapse */ TelemetryService.ɵfac = function TelemetryService_Factory(t) { return new (t || TelemetryService)(i0.ɵɵinject(i1.LoggingService, 8), i0.ɵɵinject(TELEMETRY_SERVICE_CONFIG)); };
/** @nocollapse */ /** @nocollapse */ TelemetryService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: TelemetryService, factory: TelemetryService.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TelemetryService, [{
            type: Injectable
        }], function () {
        return [{ type: i1.LoggingService, decorators: [{
                        type: Optional
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [TELEMETRY_SERVICE_CONFIG]
                    }] }];
    }, null);
})();

class TelemetryModule {
    constructor(injector, parentModule) {
        if (parentModule) {
            throw new Error('TelemetryModule is already loaded. Import it in the AppModule only');
        }
        else {
            TelemetryModule.injector = injector;
        }
    }
    static getInjector() {
        return TelemetryModule.injector;
    }
    static forRoot(loggerId) {
        return {
            ngModule: TelemetryModule,
            providers: [TelemetryService, { provide: TELEMETRY_SERVICE_CONFIG, useValue: loggerId ? loggerId : 'Telemetry' }]
        };
    }
}
TelemetryModule.injector = null;
/** @nocollapse */ /** @nocollapse */ TelemetryModule.ɵfac = function TelemetryModule_Factory(t) { return new (t || TelemetryModule)(i0.ɵɵinject(i0.Injector), i0.ɵɵinject(TelemetryModule, 12)); };
/** @nocollapse */ /** @nocollapse */ TelemetryModule.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: TelemetryModule });
/** @nocollapse */ /** @nocollapse */ TelemetryModule.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({});
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TelemetryModule, [{
            type: NgModule
        }], function () {
        return [{ type: i0.Injector }, { type: TelemetryModule, decorators: [{
                        type: Optional
                    }, {
                        type: SkipSelf
                    }] }];
    }, null);
})();

/**
 * This decorator allows to measure the [angular's lifecycles hooks]{@link https://angular.io/guide/lifecycle-hooks} for a component.
 *
 * To know about the information published see [ComponentMetric]{@link ../interfaces/ComponentMetric.html}.
 *
 * ## Example:
 * Send the information every time that MyComponent is initialized and destroyed.
 *
 * ```ts
 * \@TelemetryComponent(['ngOnInit','ngOnDestroy'])
 * \@Component({
 *    ...
 * })
 * class MyComponent {
 *    ...
 * }
 * ```
 * @param hooks List of angular's lifecycles hooks to measure
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function TelemetryComponent(hooks) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (originalConstructor) => {
        decorateClass(originalConstructor, hooks);
    };
}
/**
 * @ignore
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
function decorateClass(constructor, hooks) {
    hooks.forEach((hook) => {
        const originalMethod = constructor.prototype[hook];
        constructor.prototype[hook] = function () {
            const started = Date.now();
            if (originalMethod) {
                originalMethod.apply(this, arguments);
            }
            const duration = Date.now() - started;
            TelemetryModule.getInjector()
                .get(TelemetryService)
                .component({
                component: constructor.name,
                duration: duration,
                hook: hook
            });
        };
    });
}

/**
 * This decorator allows to measure the time of execution for a method.
 *
 * To know about the information published see [MethodMetric]{@link ../interfaces/MethodMetric.html}.
 *
 * ## Example:
 * Measure the time of execution every time that myMethod is called.
 *
 * ```ts
 * \@TelemetryMethod()
 * public myMethod(): void {
 *    ...
 * }
 * ```
 */
function TelemetryMethod() {
    return function (target, propertyKey, descriptor) {
        const originalMethod = descriptor.value;
        descriptor.value = function () {
            const started = Date.now();
            const result = originalMethod.apply(this, arguments);
            const duration = Date.now() - started;
            TelemetryModule.getInjector()
                .get(TelemetryService)
                .method({
                arguments: JSON.stringify(arguments),
                component: target.constructor.name,
                duration: duration,
                method: propertyKey,
                result: JSON.stringify(result)
            });
            return result;
        };
        return descriptor;
    };
}

class TelemetryInterceptor {
    constructor(telemetryService) {
        this.telemetryService = telemetryService;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    intercept(req, next) {
        const started = Date.now();
        let status = null;
        let errorMessage = null;
        return next.handle(req).pipe(tap(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (event) => {
            if (event instanceof HttpResponse) {
                status = event.status;
            }
        }, 
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error) => {
            if (error instanceof HttpErrorResponse) {
                status = error.status;
                errorMessage = error.message;
            }
        }), finalize(() => {
            this.telemetryService.http({
                method: req.method,
                url: req.url,
                params: req.params.toString(),
                body: req.body,
                status,
                duration: Date.now() - started,
                errorMessage
            });
        }));
    }
}
/** @nocollapse */ /** @nocollapse */ TelemetryInterceptor.ɵfac = function TelemetryInterceptor_Factory(t) { return new (t || TelemetryInterceptor)(i0.ɵɵinject(TelemetryService)); };
/** @nocollapse */ /** @nocollapse */ TelemetryInterceptor.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: TelemetryInterceptor, factory: TelemetryInterceptor.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TelemetryInterceptor, [{
            type: Injectable
        }], function () { return [{ type: TelemetryService }]; }, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { TelemetryComponent, TelemetryInterceptor, TelemetryMethod, TelemetryModule, TelemetryService };
